import React, { useState } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

/**
 * MigrateRegistrations - Admin page to migrate existing registrations
 * Add player names to registrations that don't have them
 */
const MigrateRegistrations = () => {
    const [isRunning, setIsRunning] = useState(false);
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState({ updated: 0, skipped: 0, errors: 0, total: 0 });

    const addLog = (message, type = 'info') => {
        setLogs(prev => [...prev, { message, type, timestamp: new Date().toLocaleTimeString() }]);
    };

    const runMigration = async () => {
        setIsRunning(true);
        setLogs([]);
        setStats({ updated: 0, skipped: 0, errors: 0, total: 0 });
        
        addLog('🚀 Starting migration of registrations...', 'info');
        
        try {
            // Get all registrations
            const registrationsRef = collection(db, 'registrations');
            const registrationsSnapshot = await getDocs(registrationsRef);
            
            const total = registrationsSnapshot.size;
            setStats(prev => ({ ...prev, total }));
            addLog(`📊 Found ${total} registrations to process`, 'info');
            
            let updated = 0;
            let skipped = 0;
            let errors = 0;
            
            for (const regDoc of registrationsSnapshot.docs) {
                const regData = regDoc.data();
                const regId = regDoc.id;
                
                // Skip if already has player1Name
                if (regData.player1Name) {
                    addLog(`⏭️  Skipping ${regId} - already has player1Name`, 'skip');
                    skipped++;
                    setStats({ updated, skipped, errors, total });
                    continue;
                }
                
                try {
                    let player1Name = 'Jugador sin nombre';
                    let player2Name = regData.partnerName || null;
                    
                    // Fetch player1 name from users collection
                    if (regData.player1Id) {
                        const userDocRef = doc(db, 'users', regData.player1Id);
                        const userDocSnap = await getDoc(userDocRef);
                        
                        if (userDocSnap.exists()) {
                            const userData = userDocSnap.data();
                            player1Name = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
                            
                            if (!player1Name) {
                                player1Name = userData.email || 'Jugador sin nombre';
                            }
                        } else {
                            addLog(`⚠️  User profile not found for player1Id: ${regData.player1Id}`, 'warning');
                        }
                    }
                    
                    // Update the registration
                    const regRef = doc(db, 'registrations', regId);
                    await updateDoc(regRef, {
                        player1Name: player1Name,
                        player2Name: player2Name
                    });
                    
                    addLog(`✅ Updated ${regId}: ${player1Name}${player2Name ? ' + ' + player2Name : ''}`, 'success');
                    updated++;
                    setStats({ updated, skipped, errors, total });
                    
                } catch (error) {
                    addLog(`❌ Error updating ${regId}: ${error.message}`, 'error');
                    errors++;
                    setStats({ updated, skipped, errors, total });
                }
            }
            
            addLog('✨ Migration completed!', 'success');
            toast.success(`Migration completed! Updated: ${updated}, Skipped: ${skipped}, Errors: ${errors}`);
            
        } catch (error) {
            addLog(`💥 Fatal error during migration: ${error.message}`, 'error');
            toast.error('Migration failed: ' + error.message);
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                    <h1 className="text-3xl font-black text-gray-900 mb-2">Migración de Registros</h1>
                    <p className="text-gray-600 mb-8">
                        Esta herramienta actualiza todos los registros existentes para añadir los nombres de los jugadores.
                    </p>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 mb-8">
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                            <div className="text-2xl font-black text-blue-600">{stats.total}</div>
                            <div className="text-xs text-blue-600 font-bold uppercase">Total</div>
                        </div>
                        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                            <div className="text-2xl font-black text-green-600">{stats.updated}</div>
                            <div className="text-xs text-green-600 font-bold uppercase">Actualizados</div>
                        </div>
                        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
                            <div className="text-2xl font-black text-yellow-600">{stats.skipped}</div>
                            <div className="text-xs text-yellow-600 font-bold uppercase">Omitidos</div>
                        </div>
                        <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                            <div className="text-2xl font-black text-red-600">{stats.errors}</div>
                            <div className="text-xs text-red-600 font-bold uppercase">Errores</div>
                        </div>
                    </div>

                    {/* Action Button */}
                    <button
                        onClick={runMigration}
                        disabled={isRunning}
                        className={`w-full py-4 px-6 rounded-xl font-bold text-white text-lg transition-all ${
                            isRunning
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-primary-600 hover:bg-primary-700 shadow-lg hover:shadow-xl'
                        }`}
                    >
                        {isRunning ? '⏳ Ejecutando migración...' : '🚀 Ejecutar Migración'}
                    </button>

                    {/* Logs */}
                    {logs.length > 0 && (
                        <div className="mt-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Registro de Actividad</h2>
                            <div className="bg-gray-900 rounded-xl p-4 max-h-96 overflow-y-auto font-mono text-sm">
                                {logs.map((log, index) => (
                                    <div
                                        key={index}
                                        className={`mb-1 ${
                                            log.type === 'error'
                                                ? 'text-red-400'
                                                : log.type === 'success'
                                                ? 'text-green-400'
                                                : log.type === 'warning'
                                                ? 'text-yellow-400'
                                                : log.type === 'skip'
                                                ? 'text-gray-500'
                                                : 'text-gray-300'
                                        }`}
                                    >
                                        <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MigrateRegistrations;
